import { useState, useEffect, useCallback } from 'react';
import api from '../api';

export const usePWA = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [pushSubscription, setPushSubscription] = useState(null);
  const [badgeCount, setBadgeCount] = useState(0);

  // Détection si l'app est déjà installée
  useEffect(() => {
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isInWebAppiOS = window.navigator.standalone === true;
      setIsInstalled(isStandalone || isInWebAppiOS);
    };

    checkInstalled();
  }, []);

  // Écoute de l'événement beforeinstallprompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('[PWA] App installable détectée');
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Écoute de l'événement appinstalled
  useEffect(() => {
    const handler = () => {
      console.log('[PWA] App installée');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handler);

    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  // Vérifier le statut de la permission notifications
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Installer l'application
  const installApp = useCallback(async () => {
    if (!deferredPrompt) {
      console.warn('[PWA] Aucun prompt d\'installation disponible');
      return false;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`[PWA] Choix utilisateur: ${outcome}`);
    
    if (outcome === 'accepted') {
      setIsInstallable(false);
      setDeferredPrompt(null);
      return true;
    }
    
    return false;
  }, [deferredPrompt]);

  // Demander la permission pour les notifications
  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.warn('[PWA] Notifications non supportées');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    
    if (permission === 'granted') {
      console.log('[PWA] Permission notifications accordée');
      return true;
    }
    
    console.log('[PWA] Permission notifications refusée');
    return false;
  }, []);

  // S'abonner aux push notifications
  const subscribeToPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('[PWA] Push notifications non supportées');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Vérifier si déjà abonné
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Créer un nouvel abonnement
        // Note: La clé publique VAPID doit être fournie par le backend
        const response = await api.get('/push/vapid-public-key');
        const vapidPublicKey = response.data.publicKey;
        
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
        
        console.log('[PWA] Nouvel abonnement push créé');
        
        // Envoyer l'abonnement au backend
        await api.post('/push/subscribe', subscription.toJSON());
      }
      
      setPushSubscription(subscription);
      return subscription;
    } catch (error) {
      console.error('[PWA] Erreur abonnement push:', error);
      return null;
    }
  }, []);

  // Se désabonner des push notifications
  const unsubscribeFromPush = useCallback(async () => {
    if (!pushSubscription) return;

    try {
      await pushSubscription.unsubscribe();
      
      // Informer le backend
      await api.post('/push/unsubscribe', pushSubscription.toJSON());
      
      setPushSubscription(null);
      console.log('[PWA] Désabonnement push effectué');
    } catch (error) {
      console.error('[PWA] Erreur désabonnement push:', error);
    }
  }, [pushSubscription]);

  // Mettre à jour le badge
  const updateBadge = useCallback(async (count) => {
    setBadgeCount(count);

    if (!('setAppBadge' in navigator)) {
      console.warn('[PWA] Badge API non supportée');
      return;
    }

    try {
      if (count === 0) {
        await navigator.clearAppBadge();
        console.log('[PWA] Badge effacé');
      } else {
        await navigator.setAppBadge(count);
        console.log(`[PWA] Badge mis à jour: ${count}`);
      }

      // Informer le Service Worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'UPDATE_BADGE',
          count
        });
      }
    } catch (error) {
      console.error('[PWA] Erreur mise à jour badge:', error);
    }
  }, []);

  // Effacer le badge
  const clearBadge = useCallback(async () => {
    await updateBadge(0);
  }, [updateBadge]);

  // Envoyer une notification locale
  const showNotification = useCallback(async (title, options = {}) => {
    if (Notification.permission !== 'granted') {
      console.warn('[PWA] Permission notifications non accordée');
      return;
    }

    if (!('serviceWorker' in navigator)) {
      // Fallback: notification navigateur directe
      new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        ...options
      });
      return;
    }

    // Utiliser le Service Worker pour les notifications
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      ...options
    });
  }, []);

  return {
    // États
    isInstallable,
    isInstalled,
    notificationPermission,
    pushSubscription,
    badgeCount,
    
    // Méthodes
    installApp,
    requestNotificationPermission,
    subscribeToPush,
    unsubscribeFromPush,
    updateBadge,
    clearBadge,
    showNotification
  };
};

// Utilitaire pour convertir la clé VAPID
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default usePWA;
